import { IconButton, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { makeStyles } from 'tss-react/mui';
import React from 'react';
import Colors from '../../../utils/color';
import TruncationTooltip from './TruncationTooltip';

const useStyles = makeStyles<{ isSelected: boolean; is86ed: boolean }>()(
  (_theme, { isSelected, is86ed }) => ({
    // 24px borderless row, against the 40px chip locked to `width: 18%` it
    // replaces. The box was worth about 1px of row height, so this is about ink,
    // not space.
    row: {
      display: 'flex',
      alignItems: 'center',
      gap: 4,
      minWidth: 0,
      height: 24,
      borderRadius: 2,
      cursor: is86ed ? 'not-allowed' : 'pointer',
      color: is86ed ? Colors.Thunderbird : Colors.daintree,
      fontWeight: isSelected ? 700 : 400,
      // Which row the pointer is on. The rows are borderless and tightly
      // packed, so without this there is nothing to tell the agent what a click
      // is about to land on.
      '&:hover': {
        backgroundColor: is86ed ? 'transparent' : 'rgba(0, 0, 0, 0.06)',
      },
    },
    // Moved to the LEFT edge, where the selection tick used to sit: the group
    // row's own caret is on the left, so a marker on the right made the two
    // rows disagree about where a disclosure lives.
    //
    // And it rotates, exactly as `ModGroupRow.caret` does. Selecting an option
    // is what reveals its further groups — `RecursiveModGroup` renders the
    // children of options the cart holds — so a selected option's groups are
    // open below it. Sharing the glyph with the group caret while staying
    // permanently side-on made the same mark mean "collapsed" in one row and
    // nothing at all in the next.
    furtherGroups: {
      display: 'inline-block',
      transform: isSelected ? 'rotate(90deg)' : 'none',
      transition: 'transform 120ms ease',
      flexShrink: 0,
      // 8px, and the glyph centred in it. The box has to be a fixed width so
      // every name in the drawer starts at the same x, but › is only about
      // 4px wide — at 12px and left-aligned it left ~8px of dead space inside
      // the box, on top of the row gap, which read as a large hole between the
      // marker and the name.
      width: 8,
      textAlign: 'center',
      fontSize: '12px',
      color: Colors.silverChalice,
    },
    // The strike belongs to the NAME, not the row. On the row it also drew
    // through the fixed-width marker cell to the left of the text, which
    // rendered as a stray red dash in front of every unavailable option.
    name: {
      // `0 1 auto`, NOT `1`: at flex-grow 1 the name stretched to the full
      // column width and shoved the info button out to the far right edge,
      // a long way from the option it belongs to — and, in a 2 or 3 column
      // drawer, closer to the NEXT column's rows than to its own text. Sized to
      // its content, the button follows the name with the row's own 4px gap,
      // and still shrinks with an ellipsis when the name is too long.
      flex: '0 1 auto',
      minWidth: 0,
      fontSize: '13px',
      textDecoration: is86ed ? 'line-through' : 'none',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    info: {
      padding: 0,
      color: 'inherit',
    },
    // The tooltip needs a live anchor. MUI gives a disabled button
    // `pointer-events: none`, so with the button as the tooltip's direct child
    // the hover would be swallowed once the option is 86'd (and MUI logs a
    // warning for it). The span takes the hover; the button takes the press.
    infoWrap: {
      flexShrink: 0,
      display: 'inline-flex',
    },
  })
);

interface ModOptionRowProps {
  name: string;
  isSelected: boolean;
  is86ed: boolean;
  bringsFurtherGroups: boolean;
  onSelect: () => void;
  /** The option's spoken description, when it has one. */
  description?: string;
  /** Reads `description` out to the customer. Absent when there is none. */
  onInfoClick?: () => void;
}

/**
 * HITL-1616 — one option inside the drawer.
 *
 * Selection is weight alone: the tick that used to sit on the left was a fixed
 * 12px of gutter on every row, spent to say something bold already says. The
 * `›` disclosure took its place so it lines up with the group row's caret.
 *
 * An unavailable option is red + line-through with **no "86" badge** — the
 * `SingularModChoice` precedent, kept.
 *
 * The name is printed in the menu's own case, NOT uppercased the way the chips
 * were. Caps read measurably slower and run about 10-15% wider, and these are
 * `minmax(186px, 1fr)` columns that ellipsise — so uppercasing would truncate
 * the long names this row exists to spell out. And never vowel-stripped:
 * `getModGroupItemLabel` rendered "Substitute Sugar Free Vanilla" as
 * `Sbsttt Sgr Fr Vnll`.
 *
 * No price. The frozen design carries none, and the console could not show a
 * correct one anyway: both chip callers pass no `modalityState`, so
 * `getMenuItemPrice` falls back to `ModalityType.Dinein` on a drive-thru. Prices
 * stay where they are true — the cart line.
 */
const ModOptionRow = ({
  name,
  isSelected,
  is86ed,
  bringsFurtherGroups,
  onSelect,
  description,
  onInfoClick,
}: ModOptionRowProps) => {
  const { classes } = useStyles({ isSelected, is86ed });

  const onClick = () => {
    if (is86ed) return;
    onSelect();
  };

  return (
    <div
      className={classes.row}
      onClick={onClick}
      data-testid="mod-option-row"
      data-selected={isSelected ? 'true' : 'false'}
      role="button"
      tabIndex={is86ed ? -1 : 0}
      aria-disabled={is86ed}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') onClick();
      }}
    >
      {/*
        Always rendered, so every name in the drawer starts at the same x
        whether or not its option opens further groups. The test id is attached
        only when the marker is really there — on an empty spacer it would make
        the "brings further groups" assertion pass for nothing.
      */}
      <span
        className={classes.furtherGroups}
        data-testid={bringsFurtherGroups ? 'brings-groups' : undefined}
        aria-hidden={!bringsFurtherGroups}
      >
        {bringsFurtherGroups ? '›' : ''}
      </span>
      <TruncationTooltip title={name}>
        <span className={classes.name}>{name}</span>
      </TruncationTooltip>
      {onInfoClick && (
        <Tooltip title={description || ''} arrow placement="top">
          <span className={classes.infoWrap}>
            <IconButton
              size="small"
              className={classes.info}
              // An unavailable option cannot be ordered, so its description
              // must not be speakable to the customer either — the row itself
              // already refuses the click, and this closes the one control that
              // still reached past it. Kept rendered rather than removed, so
              // the row still shows that a description exists.
              disabled={is86ed}
              // The row toggles the selection, so without this the icon would
              // pick the option as well as speak it.
              onClick={(event) => {
                event.stopPropagation();
                onInfoClick();
              }}
              aria-label={`Play description for ${name}`}
            >
              <InfoOutlinedIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </div>
  );
};

export default ModOptionRow;
